import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

// How long before the access token's `exp` claim to fire a silent refresh,
// so an idle tab never meets a dead token.
const PROACTIVE_REFRESH_BUFFER_MS = 60 * 1000;

// Decode the `exp` claim (seconds since epoch) out of a JWT without
// verifying its signature — client-side, so verification adds nothing;
// this is only used to schedule a timer.
function decodeJwtExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const decoded = JSON.parse(json);
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
}

export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  role: 'user' | 'eskimi_admin' | 'adops' | 'admin';
  provider: 'eskimi' | 'google' | 'local';
  emailVerified: boolean;
  createdAt: string;
  companyId?: string | null;
}

interface ImpersonationInfo {
  active: boolean;
  adminId?: string;
  adminEmail?: string;
  adminName?: string;
  startedAt?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  impersonation: ImpersonationInfo | null;
  login: (provider?: 'eskimi' | 'google') => void;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Create axios instance with interceptors
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Important for refresh token cookies
});

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [impersonation, setImpersonation] = useState<ImpersonationInfo | null>(null);

  const refreshTokenRef = useRef<(() => Promise<boolean>) | null>(null);
  const refreshInFlightRef = useRef<Promise<boolean> | null>(null);
  const proactiveRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleProactiveRefresh = (token: string) => {
    if (proactiveRefreshTimeoutRef.current) {
      clearTimeout(proactiveRefreshTimeoutRef.current);
      proactiveRefreshTimeoutRef.current = null;
    }

    const exp = decodeJwtExpiry(token);
    if (exp === null) {
      return;
    }

    const delayMs = Math.max(0, exp * 1000 - Date.now() - PROACTIVE_REFRESH_BUFFER_MS);
    proactiveRefreshTimeoutRef.current = setTimeout(() => {
      refreshTokenRef.current?.();
    }, delayMs);
  };

  useEffect(() => {
    const storedToken = localStorage.getItem('accessToken');
    if (storedToken) {
      scheduleProactiveRefresh(storedToken);
    }
    return () => {
      if (proactiveRefreshTimeoutRef.current) {
        clearTimeout(proactiveRefreshTimeoutRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const requestId = api.interceptors.request.use((config) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    const redirectToLogin = () => {
      // Session is gone. Bounce to /login so the user isn't stranded on a
      // half-broken page while background XHRs 401 silently. Full-page
      // navigation (not useNavigate) resets all in-memory auth state and
      // stale provider caches. Skip if we are already on /login to avoid a
      // redirect loop.
      localStorage.removeItem('accessToken');
      if (window.location.pathname !== '/login') {
        const returnTo = encodeURIComponent(
          window.location.pathname + window.location.search
        );
        window.location.assign(`/login?returnTo=${returnTo}`);
      }
    };

    const responseId = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401) {
          if (!originalRequest._retry) {
            originalRequest._retry = true;

            const success = await refreshTokenRef.current?.();
            if (success) {
              const token = localStorage.getItem('accessToken');
              if (token) {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
              return api.request(originalRequest);
            }

            // Refresh failed — bounce to /login.
            redirectToLogin();
          } else {
            // Retry already attempted but server still says 401 (e.g. token
            // revoked / token-version bump between refresh and retry).
            // Don't strand the user on the broken page — bounce.
            redirectToLogin();
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestId);
      api.interceptors.response.eject(responseId);
    };
  }, []);

  useEffect(() => {
    initializeAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initializeAuth = async () => {
    try {
      setIsLoading(true);
      
      // Check if we have an access token
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Try to get user info with current token
      try {
        const response = await api.get('/api/auth/me');
        setUser(response.data.user);
        setIsAuthenticated(true);
        
        // Check impersonation status
        const impersonationResponse = await api.get('/api/impersonation/status');
        if (impersonationResponse.data.impersonating) {
          setImpersonation({
            active: true,
            adminId: impersonationResponse.data.adminUser.id,
            adminEmail: impersonationResponse.data.adminUser.email,
            adminName: impersonationResponse.data.adminUser.name,
            startedAt: impersonationResponse.data.startedAt
          });
        }
      } catch (error) {
        // Interceptor already attempted token refresh on 401;
        // if we're here, it failed or wasn't an auth error.
        localStorage.removeItem('accessToken');
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      localStorage.removeItem('accessToken');
    } finally {
      setIsLoading(false);
    }
  };

  const login = (provider: 'eskimi' | 'google' = 'google') => {
    // Redirect to OAuth login
    // If we're on the login page, return to home instead
    const currentPath = window.location.pathname;
    const returnTo = encodeURIComponent(currentPath === '/login' ? '/' : currentPath);
    // OAuth routes are at root level, not under /api/
    const oauthUrl = process.env.NODE_ENV === 'production'
      ? `/oauth/${provider}?returnTo=${returnTo}`
      : `${API_BASE_URL}/oauth/${provider}?returnTo=${returnTo}`;

    // Force full page navigation to bypass React Router
    window.location.replace(oauthUrl);
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless of API call result
      localStorage.removeItem('accessToken');
      setUser(null);
      setIsAuthenticated(false);
      
      // Redirect to home page
      window.location.href = '/';
    }
  };

  const refreshToken = (): Promise<boolean> => {
    // Concurrent 401s (or a 401 racing the proactive timer) must share one
    // in-flight refresh: the backend rotates the refresh token on every
    // call, so a second refresh started before the first settles would
    // present an already-rotated token and get rejected.
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const attempt = (async (): Promise<boolean> => {
      try {
        // Use raw axios to avoid triggering the response interceptor loop
        const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {}, {
          withCredentials: true,
        });
        const { accessToken } = response.data;

        localStorage.setItem('accessToken', accessToken);
        scheduleProactiveRefresh(accessToken);
        console.log('✅ Token refreshed successfully');
        return true;
      } catch (error) {
        console.error('❌ Token refresh failed:', error);
        return false;
      }
    })();

    refreshInFlightRef.current = attempt.finally(() => {
      refreshInFlightRef.current = null;
    });

    return refreshInFlightRef.current;
  };

  refreshTokenRef.current = refreshToken;

  const startImpersonation = async (userId: string) => {
    try {
      const response = await api.post('/api/impersonation/start', { userId });
      
      if (response.data.success) {
        // Update token
        localStorage.setItem('accessToken', response.data.token);
        
        // Update user state
        setUser(response.data.targetUser);
        
        // Set impersonation state
        setImpersonation({
          active: true,
          adminId: user?.id,
          adminEmail: user?.email,
          adminName: user?.name,
          startedAt: new Date().toISOString()
        });
        
        // Reload the page to ensure all components use the new context
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      const response = await api.post('/api/impersonation/stop');
      
      if (response.data.success) {
        // Update token
        localStorage.setItem('accessToken', response.data.token);
        
        // Update user state
        setUser(response.data.user);
        
        // Clear impersonation state
        setImpersonation(null);
        
        // Reload the page to ensure all components use the new context
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    impersonation,
    login,
    logout,
    refreshToken,
    startImpersonation,
    stopImpersonation,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Export the configured axios instance for use in other components
export { api };

export default AuthContext;